import React, { useCallback, useEffect, useState } from 'react';
import { View, Text, TouchableOpacity, StyleSheet, ActivityIndicator } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { colors, spacing, typography } from '../../theme';
import { designTokens } from '../../theme/design-system';
import { useThemedStyles } from '../../hooks/useThemedStyles';
import { useRTLStyles } from '../../hooks/useRTLStyles';
import { useLocale } from '../../contexts/LocaleContext';
import { useAppContext } from '../../contexts/AppContext';
import { usePermissions } from '../../hooks/usePermissions';
import { apiService } from '../../services/api';
import { EntityPickerModal } from '../EntityPickerModal';

interface LinkedVendor {
  _id: string;
  name: string;
  phone?: string;
  email?: string;
}

export interface ProductVendorsSectionProps {
  productId: string;
  /** Product.vendorIds as loaded with the product. */
  vendorIds: string[];
}

/**
 * Product edit: which vendors supply this product. Everyone sees the list
 * (tap opens the vendor); linking/unlinking uses the same vendor<->product
 * endpoints and the same general.manageVendors 'edit' gate as the vendor
 * screen's own "Manage Products", so both sides stay in sync.
 */
export const ProductVendorsSection: React.FC<ProductVendorsSectionProps> = ({ productId, vendorIds }) => {
  const styles = useThemedStyles(getStyles);
  const rtl = useRTLStyles();
  const { t } = useLocale();
  const { navigateTo, showNotification } = useAppContext();
  const { hasPermissionAtLeast } = usePermissions();
  const canManage = hasPermissionAtLeast('general.manageVendors', 'edit');

  const [ids, setIds] = useState<string[]>(vendorIds);
  const [vendors, setVendors] = useState<LinkedVendor[]>([]);
  const [loading, setLoading] = useState(vendorIds.length > 0);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [showPicker, setShowPicker] = useState(false);

  useEffect(() => setIds(vendorIds), [vendorIds]);

  const loadVendors = useCallback(async (list: string[]) => {
    if (list.length === 0) {
      setVendors([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    const results = await Promise.all(list.map((id) => apiService.getVendorById(id).catch(() => null)));
    // Deleted/inaccessible vendors simply drop out of the list.
    setVendors(results.filter((r: any) => r?.statusCode === 1000 && r.data).map((r: any) => r.data as LinkedVendor));
    setLoading(false);
  }, []);

  useEffect(() => {
    loadVendors(ids);
  }, [ids, loadVendors]);

  const handleAdd = async (selected: string[]) => {
    setShowPicker(false);
    const toAdd = selected.filter((id) => !ids.includes(id));
    if (toAdd.length === 0) return;
    setBusyId('add');
    const results = await Promise.all(toAdd.map((id) => apiService.addProductToVendor(id, productId).then((r) => r.statusCode === 1000).catch(() => false)));
    const added = toAdd.filter((_, i) => results[i]);
    setBusyId(null);
    if (added.length > 0) setIds((prev) => [...prev, ...added]);
    if (added.length < toAdd.length) {
      showNotification('error', t('common.error'), t('productVendors.addFailed'));
    } else {
      showNotification('success', t('common.success'), t('productVendors.added', { count: added.length }));
    }
  };

  const handleRemove = async (vendorId: string) => {
    setBusyId(vendorId);
    try {
      const res = await apiService.removeProductFromVendor(vendorId, productId);
      if (res.statusCode === 1000) {
        setIds((prev) => prev.filter((id) => id !== vendorId));
      } else {
        showNotification('error', t('common.error'), t('productVendors.removeFailed'));
      }
    } catch {
      showNotification('error', t('common.error'), t('productVendors.removeFailed'));
    } finally {
      setBusyId(null);
    }
  };

  // Read-only users with nothing linked: nothing to show.
  if (!canManage && !loading && vendors.length === 0) return null;

  return (
    <View style={styles.section}>
      <View style={[styles.header, { flexDirection: rtl.rowDirection }]}>
        <Text style={[styles.title, { textAlign: rtl.textStart }]}>
          {t('productVendors.title')}{vendors.length > 0 ? ` (${vendors.length})` : ''}
        </Text>
        {canManage && (
          <TouchableOpacity style={[styles.addButton, { flexDirection: rtl.rowDirection }]} onPress={() => setShowPicker(true)} disabled={busyId === 'add'}>
            {busyId === 'add'
              ? <ActivityIndicator size="small" color={colors.primary as string} />
              : <Ionicons name="add" size={18} color={colors.primary as string} />}
            <Text style={styles.addButtonText}>{t('productVendors.add')}</Text>
          </TouchableOpacity>
        )}
      </View>

      {loading ? (
        <ActivityIndicator size="small" color={colors.primary as string} style={styles.loading} />
      ) : vendors.length === 0 ? (
        <Text style={[styles.emptyText, { textAlign: rtl.textStart }]}>{t('productVendors.empty')}</Text>
      ) : (
        vendors.map((v, index) => (
          <View key={v._id} style={[styles.row, { flexDirection: rtl.rowDirection }, index > 0 && styles.rowDivider]}>
            <TouchableOpacity style={[styles.rowMain, { flexDirection: rtl.rowDirection }]} onPress={() => navigateTo('vendor-detail', v._id)} activeOpacity={0.7}>
              <View style={styles.avatar}>
                <Ionicons name="business-outline" size={16} color={colors.primary as string} />
              </View>
              <View style={styles.rowText}>
                <Text style={[styles.vendorName, { textAlign: rtl.textStart }]} numberOfLines={1}>{v.name}</Text>
                {!!(v.phone || v.email) && (
                  <Text style={[styles.vendorMeta, { textAlign: rtl.textStart }]} numberOfLines={1}>{v.phone || v.email}</Text>
                )}
              </View>
            </TouchableOpacity>
            {canManage && (
              busyId === v._id
                ? <ActivityIndicator size="small" color={colors.error as string} />
                : (
                  <TouchableOpacity onPress={() => handleRemove(v._id)} hitSlop={8} accessibilityLabel={t('productVendors.remove')}>
                    <Ionicons name="close-circle-outline" size={20} color={colors.error as string} />
                  </TouchableOpacity>
                )
            )}
          </View>
        ))
      )}

      {canManage && (
        <EntityPickerModal
          visible={showPicker}
          onClose={() => setShowPicker(false)}
          onConfirm={handleAdd}
          title={t('productVendors.pickerTitle')}
          confirmText={t('productVendors.pickerConfirm')}
          allowMultiple
          fetchItems={async ({ search, skip, limit }) => {
            const res = await apiService.getVendors({ search: search || undefined, skip, limit });
            const page = res.data?.vendors || [];
            const total = res.data?.pagination?.total || 0;
            return {
              items: page.filter((v: any) => !ids.includes(v._id)).map((v: any) => ({ id: v._id, label: v.name, subtitle: v.phone || v.email })),
              hasMore: skip + page.length < total,
            };
          }}
        />
      )}
    </View>
  );
};

const getStyles = () => StyleSheet.create({
  section: {
    marginTop: spacing.lg,
    padding: spacing.md,
    borderRadius: designTokens.borderRadius.md,
    borderWidth: 1,
    borderColor: colors.border,
    backgroundColor: colors.background.surface,
  },
  header: { alignItems: 'center', justifyContent: 'space-between', marginBottom: spacing.xs },
  title: { flexShrink: 1, fontSize: typography.fontSize.base, fontFamily: typography.fontFamily.semibold, color: colors.text.primary },
  addButton: { alignItems: 'center', gap: spacing.xs },
  addButtonText: { fontSize: typography.fontSize.sm, fontFamily: typography.fontFamily.medium, color: colors.primary },
  loading: { marginVertical: spacing.sm },
  emptyText: { fontSize: typography.fontSize.sm, fontFamily: typography.fontFamily.regular, color: colors.text.secondary, paddingVertical: spacing.xs },
  row: { alignItems: 'center', gap: spacing.sm, paddingVertical: spacing.sm },
  rowDivider: { borderTopWidth: 1, borderTopColor: colors.border },
  rowMain: { flex: 1, alignItems: 'center', gap: spacing.sm },
  avatar: { width: 32, height: 32, borderRadius: 16, alignItems: 'center', justifyContent: 'center', backgroundColor: colors.background.secondary },
  rowText: { flex: 1, minWidth: 0 },
  vendorName: { fontSize: typography.fontSize.sm, fontFamily: typography.fontFamily.semibold, color: colors.text.primary },
  vendorMeta: { fontSize: typography.fontSize.xs, fontFamily: typography.fontFamily.regular, color: colors.text.secondary, marginTop: 1 },
});
